import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  IconButton,
  LinearProgress,
  Button,
  Tooltip,
} from "@mui/material";
import {
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
} from "@mui/icons-material";
import styled from "@emotion/styled";
import ParkkiTheme from "themes/ParkkiTheme";
import { connect } from "react-redux";
import LiveActioner from "services/live/actioner";
import { useIntl } from "react-intl";
import { AlertPoliciesUtils } from "../../../services/alertFactory/alertPolicies/utils";
import moment from "moment";
import Draggable from "react-draggable";
import Paper from "@mui/material/Paper";
import { AlertEventsActioner } from "../../../services/alertFactory/alertEvents/actioner";
import { AlertEventsUtils } from "../../../services/alertFactory/alertEvents/utils";

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 12px;
    max-width: 800px;
    width: 100%;
  }
`;

const StyledDialogTitle = styled(DialogTitle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${ParkkiTheme.spacings.base} ${ParkkiTheme.spacings.base}
    ${ParkkiTheme.spacings.small};
  border-bottom: 1px solid ${ParkkiTheme.colors.lightGrey};

  .MuiTypography-root {
    display: flex;
    align-items: center;
    gap: ${ParkkiTheme.spacings.small};
    font-size: ${ParkkiTheme.fontSizes.title3};
    font-weight: 600;
    color: ${ParkkiTheme.colors.blue};
  }
`;

const StyledDialogContent = styled(DialogContent)`
  margin-top: ${ParkkiTheme.spacings.small};

  .alert-message {
    margin-bottom: ${ParkkiTheme.spacings.tiny};
    font-size: ${ParkkiTheme.fontSizes.large};
    line-height: 1.6;
    color: ${ParkkiTheme.colors.darkGrey};
    background-color: ${ParkkiTheme.colors.white};
    padding: ${ParkkiTheme.spacings.base};
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    font-weight: bold;
  }

  .alert-counter {
    text-align: center;
    margin-bottom: ${ParkkiTheme.spacings.base};
    color: ${ParkkiTheme.colors.grey};
    font-size: ${ParkkiTheme.fontSizes.small};
  }

  .alert-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${ParkkiTheme.spacings.small};
  }

  .close-alert-button {
    color: ${ParkkiTheme.colors.red};
    &:hover {
      background-color: ${ParkkiTheme.colors.lightGrey};
    }
  }
`;

const StyledDialogActions = styled(DialogActions)`
  padding: ${ParkkiTheme.spacings.small} ${ParkkiTheme.spacings.base}
    ${ParkkiTheme.spacings.base};
  border-top: 1px solid ${ParkkiTheme.colors.lightGrey};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AutoCloseProgress = styled(LinearProgress)`
  height: 3px;
  width: 100%;

  .MuiLinearProgress-bar {
    background-color: ${ParkkiTheme.colors.red};
  }
`;

const NavigationButtons = styled(Box)`
  display: flex;
  gap: ${ParkkiTheme.spacings.small};
  align-items: center;
`;

function PaperComponent({ position, setAlertModalPosition, ...props }) {
  const nodeRef = React.useRef(null);
  const [currentPosition, setPosition] = useState(position ?? { x: 0, y: 0 });
  const handleDrag = (e, data) => {
    setPosition({ x: data.x, y: data.y });
  };
  const handleStop = () => {
    setAlertModalPosition(currentPosition);
  };
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      onDrag={handleDrag}
      onStop={handleStop}
      position={currentPosition}
    >
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}

const ModalPaperComponent = connect(
  (state) => ({
    position: state.Live.alertModalPosition,
  }),
  (dispatch) => ({
    setAlertModalPosition: (position) =>
      dispatch(LiveActioner.setAlertModalPosition(position)),
  })
)(PaperComponent);

const AlertModalComponent = ({ alertEvents, onClose, onCloseAlert }) => {
  const open = alertEvents.length > 0;
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);
  const [alertTimers, setAlertTimers] = useState({});
  const [storedAlerts, setStoredAlerts] = useState([]);
  const [closedAlertIds, setClosedAlertIds] = useState(new Set());

  const intl = useIntl();

  const allAlerts = storedAlerts;
  const currentAlert = allAlerts[currentAlertIndex];

  useEffect(() => {
    if (alertEvents.length > 0) {
      setStoredAlerts((prev) => {
        const newAlerts = alertEvents.filter(
          (newAlert) =>
            !prev.some((storedAlert) => storedAlert.id === newAlert.id) &&
            !closedAlertIds.has(newAlert.id)
        );

        return [...prev, ...newAlerts];
      });
    }
  }, [alertEvents, closedAlertIds]);

  useEffect(() => {
    if (alertEvents.length > 0 && storedAlerts.length === 0) {
      setCurrentAlertIndex(0);
    }
  }, [alertEvents, storedAlerts.length]);

  const getCurrentAlertAutoCloseDelay = useCallback(() => {
    if (!currentAlert) return 0;
    const autoCloseOutput = currentAlert?.outputs?.find(
      (output) => output.type === "BO_TOAST" && output.auto_close_in_seconds
    );
    return autoCloseOutput?.auto_close_in_seconds || 0;
  }, [currentAlert]);

  const isAlertAutoClose = useMemo(() => {
    return getCurrentAlertAutoCloseDelay() > 0;
  }, [getCurrentAlertAutoCloseDelay]);

  const handleClose = useCallback(() => {
    const allAlertIds = storedAlerts.map((alert) => alert.id);

    setClosedAlertIds((prev) => new Set([...prev, ...allAlertIds]));

    setAlertTimers({});
    setStoredAlerts([]);
    setCurrentAlertIndex(0);

    onClose(allAlertIds);
  }, [onClose, storedAlerts]);

  useEffect(() => {
    const autoCloseDelay = getCurrentAlertAutoCloseDelay();
    if (autoCloseDelay <= 0 || !open || !currentAlert) return;

    const currentAlertId = currentAlert.id;
    if (!currentAlertId) return;

    setAlertTimers((prevTimers) => {
      if (!prevTimers[currentAlertId]) {
        return {
          ...prevTimers,
          [currentAlertId]: {
            timeLeft: autoCloseDelay,
            isActive: true,
          },
        };
      }
      return prevTimers;
    });

    const timer = setInterval(() => {
      setAlertTimers((prevTimers) => {
        const currentTimer = prevTimers[currentAlertId];

        if (!currentTimer || !currentTimer.isActive) {
          return prevTimers;
        }

        const updatedTimers = { ...prevTimers };
        updatedTimers[currentAlertId].timeLeft -= 1;

        if (updatedTimers[currentAlertId].timeLeft <= 0) {
          updatedTimers[currentAlertId].isActive = false;

          // Avoid react state update error
          setTimeout(() => {
            if (onCloseAlert) {
              onCloseAlert(currentAlertId);
            }

            if (allAlerts.length > 1) {
              if (currentAlertIndex < allAlerts.length - 1) {
                setCurrentAlertIndex(currentAlertIndex + 1);
              } else {
                handleClose();
              }
            } else {
              handleClose();
            }
          }, 500);
        }

        return updatedTimers;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [
    open,
    currentAlert?.id,
    onCloseAlert,
    currentAlertIndex,
    allAlerts.length,
    handleClose,
    getCurrentAlertAutoCloseDelay,
  ]);

  const handleNextAlert = () => {
    if (currentAlertIndex < allAlerts.length - 1) {
      if (onCloseAlert && currentAlert) {
        const currentAlertId = currentAlert.id;
        setAlertTimers((prev) => ({
          ...prev,
          [currentAlertId]: { ...prev[currentAlertId], isActive: false },
        }));

        setClosedAlertIds((prev) => new Set([...prev, currentAlertId]));
        onCloseAlert(currentAlertId);
      }

      setCurrentAlertIndex(currentAlertIndex + 1);
    }
  };

  const handlePrevAlert = () => {
    if (currentAlertIndex > 0) {
      if (onCloseAlert && currentAlert) {
        const currentAlertId = currentAlert.id;
        setAlertTimers((prev) => ({
          ...prev,
          [currentAlertId]: { ...prev[currentAlertId], isActive: false },
        }));

        setClosedAlertIds((prev) => new Set([...prev, currentAlertId]));
        onCloseAlert(currentAlertId);
      }

      setCurrentAlertIndex(currentAlertIndex - 1);
    }
  };

  if (allAlerts.length === 0) return null;

  const currentTimer = alertTimers[currentAlert?.id];

  if (!currentAlert) {
    return null;
  }

  return (
    <StyledDialog
      open={open}
      onClose={undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={true}
      PaperComponent={ModalPaperComponent}
      aria-labelledby="draggable-dialog-title"
    >
      <div id="draggable-dialog-title">
        <StyledDialogTitle style={{ cursor: "move" }}>
          <Typography>
            <Tooltip
              title={intl.formatMessage({
                id: `alertsScene.rules.${currentAlert?.alert_policy_config?.rule}`,
              })}
            >
              {AlertPoliciesUtils.getRuleIcon(
                currentAlert.alert_policy_config?.rule
              )}
            </Tooltip>
            {currentAlert?.alert_policy_config?.name || null}
          </Typography>
        </StyledDialogTitle>
      </div>

      <StyledDialogContent>
        <div
          className="alert-message"
          style={{
            marginTop: allAlerts.length > 1 ? 0 : "10px",
            ...AlertEventsUtils.getOutputColor(
              currentAlert.outputs.find((output) => output.type === "BO_TOAST")
            ),
          }}
        >
          {currentAlert.outputs.find((output) => output.type === "BO_TOAST")
            ?.message ||
            intl.formatMessage({
              id: "alertsScene.modal.noMessage",
            })}
        </div>
        <div
          style={{
            textAlign: "right",
            marginTop: "8px",
            fontSize: "0.8rem",
          }}
        >
          {AlertEventsUtils.formatDate(currentAlert.created_at)}
        </div>
      </StyledDialogContent>

      <StyledDialogActions>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleClose}
              sx={{
                color: ParkkiTheme.colors.primary,
                borderColor: ParkkiTheme.colors.primary,
              }}
            >
              {intl.formatMessage(
                { id: "alertsScene.modal.markAllAsRead" },
                { count: allAlerts.length }
              )}
            </Button>
          </Box>

          {allAlerts.length > 1 && (
            <NavigationButtons>
              <IconButton
                onClick={handlePrevAlert}
                disabled={currentAlertIndex === 0}
                sx={{
                  color:
                    currentAlertIndex === 0
                      ? ParkkiTheme.colors.disabledGrey
                      : ParkkiTheme.colors.grey,
                }}
              >
                <PrevIcon />
              </IconButton>
              <Typography variant="caption" color="textSecondary">
                {currentAlertIndex + 1} / {allAlerts.length}
              </Typography>
              <IconButton
                onClick={handleNextAlert}
                disabled={currentAlertIndex === allAlerts.length - 1}
                sx={{
                  color:
                    currentAlertIndex === allAlerts.length - 1
                      ? ParkkiTheme.colors.disabledGrey
                      : ParkkiTheme.colors.grey,
                }}
              >
                <NextIcon />
              </IconButton>
            </NavigationButtons>
          )}
        </Box>
      </StyledDialogActions>
      {isAlertAutoClose && currentTimer && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "right",
            mt: -3,
          }}
        >
          {currentTimer.timeLeft >= 0 && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{
                marginTop: "5px",
                textAlign: "right",
                display: "block",
                paddingRight: `${ParkkiTheme.spacings.base}`,
              }}
            >
              {intl.formatMessage(
                {
                  id: "alertsScene.modal.closeAutomatically",
                },
                {
                  timeLeft: currentTimer.timeLeft,
                }
              )}
            </Typography>
          )}
          <AutoCloseProgress
            variant="determinate"
            value={
              currentTimer.timeLeft <= 0
                ? 100
                : ((getCurrentAlertAutoCloseDelay() - currentTimer.timeLeft) /
                    getCurrentAlertAutoCloseDelay()) *
                  100
            }
          />
        </Box>
      )}
    </StyledDialog>
  );
};

export const AlertModal = connect(
  (state) => ({
    alertEvents: state.Live.alertEvents.data?.data ?? [],
  }),
  (dispatch) => ({
    onClose: (alertEventIds) => {
      dispatch(AlertEventsActioner.updateAlertEventsViewer(alertEventIds));
      dispatch(LiveActioner.setAlertEvents([]));
    },
    onCloseAlert: (alertId) => {
      dispatch(AlertEventsActioner.updateAlertEventsViewer([alertId]));
    },
  })
)(AlertModalComponent);
